import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useState, useMemo, useRef } from 'react';

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserId } from 'firebase/analytics';

import { auth, analytics, db } from './config/firebase';
import { userLogin, userLogout } from './store/user/userAction';
import {
  getUserData,
  checkUploadFCMToken,
  checkUploadBookMarks,
} from './utilities/user-data/UserData';
import { HighlightManager } from './utilities/user-data/HighlightManager';
import { RouterProvider } from 'react-router-dom';
import { initEventReminders } from './feed/rsvps';
import { router } from './router';
import EngagementTracker from './component/EngagementTracker';
import CookieConsent from './component/CookieConsent';
import { IframeRefContext } from './context/IframeRefContext';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Browser } from '@capacitor/browser';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { PushNotifications } from '@capacitor/push-notifications';
import { clearSavedFeed } from './feed/persist';

// Set global flag for iframes to detect native environment
window.isNativeApp = Capacitor.isNativePlatform();

function App() {
  const [consent, setConsent] = useState(null);
  // Show the consent UI only after a delay
  const [showConsent, setShowConsent] = useState(false);
  const [highlightManager, setHighlightManager] = useState(null);
  const dispatch = useDispatch();
  const [highlights, setHighlights] = useState([]);
  const [isIframeReady, setIsIframeReady] = useState(false);

  const iframeRef = useRef(null);

  // Event RSVP reminders (feed): re-arm on load; open the event on tap.
  useEffect(() => initEventReminders((path) => router.navigate(path)), []);

  // Handle Capacitor App Links (Deep Linking) and App State
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Prevent the webview from rendering underneath the status bar.
      StatusBar.setOverlaysWebView({ overlay: false }).catch(console.error);

      // Dynamically adapt the status bar background and icon style to match the app's Dark/Light theme
      const updateStatusBarStyle = () => {
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches;
        // Light Mode: Status bar is White, Icons are Dark (Style.Light)
        // Dark Mode: Status bar is Slate-900 (#0f172a), Icons are Light (Style.Dark)
        StatusBar.setBackgroundColor({
          color: prefersDark ? '#0f172a' : '#ffffff',
        }).catch(console.error);
        StatusBar.setStyle({
          style: prefersDark ? Style.Dark : Style.Light,
        }).catch(console.error);
      };

      // Set initial style
      updateStatusBarStyle();

      // Listen for OS theme changes
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', updateStatusBarStyle);

      const appUrlOpenListener = CapacitorApp.addListener(
        'appUrlOpen',
        async (data) => {
          console.log('App opened with URL: ' + data.url);

          if (data.url.includes('baws.in')) {
            const urlObject = new URL(data.url);
            const targetPath =
              urlObject.pathname + urlObject.search + urlObject.hash;

            // If we are at the root path, just navigate immediately
            if (window.location.pathname === '/') {
              router.navigate(targetPath);
              return;
            }

            // Otherwise, we are deep in the app (e.g. reading a book). Prompt the user.
            const result = await ActionSheet.showActions({
              title: 'Open Link',
              message:
                'You clicked a link to another part of the app. What would you like to do?',
              options: [
                {
                  title: 'Open Now (Navigate away)',
                  style: ActionSheetButtonStyle.Default,
                },
                {
                  title: 'Save Link (Read later)',
                  style: ActionSheetButtonStyle.Default,
                },
                {
                  title: 'Open in Browser',
                  style: ActionSheetButtonStyle.Default,
                },
                { title: 'Cancel', style: ActionSheetButtonStyle.Cancel },
              ],
            });

            if (result.index === 0) {
              // Open Now
              router.navigate(targetPath);
            } else if (result.index === 1) {
              // Save Link
              const existingBookmarks = JSON.parse(
                localStorage.getItem('saved_links') || '[]'
              );
              existingBookmarks.push({ url: targetPath, savedAt: Date.now() });
              localStorage.setItem(
                'saved_links',
                JSON.stringify(existingBookmarks)
              );
              alert('Link saved successfully!');
            } else if (result.index === 2) {
              // Open in Browser
              await Browser.open({ url: data.url });
            }
          }
        }
      );

      const pushListener = PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification) => {
          const data = notification.notification.data;
          // If the backend sent a link in the notification payload, route to it
          if (data && data.url) {
            const urlObject = new URL(data.url, window.location.origin);
            const targetPath =
              urlObject.pathname + urlObject.search + urlObject.hash;
            router.navigate(targetPath);
          }
        }
      );

      const appStateListener = CapacitorApp.addListener(
        'appStateChange',
        ({ isActive }) => {
          if (!isActive) {
            // App went to background, save current URL
            const currentUrl =
              window.location.pathname +
              window.location.search +
              window.location.hash;
            localStorage.setItem('last_visited_url', currentUrl);
          }
        }
      );

      const backButtonListener = CapacitorApp.addListener(
        'backButton',
        ({ canGoBack }) => {
          if (window.isActionSheetOpen) {
            // Let the native dialog close, do nothing else
            return;
          }
          // If we are at the home screen or there is no web history left,
          // minimize the app (send to background) like standard Android apps.
          if (!canGoBack || window.location.pathname === '/') {
            CapacitorApp.minimizeApp();
          } else {
            window.history.back();
          }
        }
      );

      // Restore last visited URL on boot
      const lastUrl = localStorage.getItem('last_visited_url');
      if (lastUrl && window.location.pathname === '/') {
        router.navigate(lastUrl);
      }

      return () => {
        mq.removeEventListener('change', updateStatusBarStyle);
        appUrlOpenListener.then((listener) => listener.remove());
        pushListener.then((listener) => listener.remove());
        appStateListener.then((listener) => listener.remove());
        backButtonListener.then((listener) => listener.remove());
      };
    }
  }, []);

  // Global Long-Press Interceptor for Native WebViews
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleContextMenu = async (e) => {
        const link = e.target.closest('a');
        if (link && link.href) {
          e.preventDefault(); // Stop native context menu from flashing

          // Convert localhost to baws.in for copying natively
          let actualUrl = link.href;
          if (
            actualUrl.includes('localhost') ||
            actualUrl.includes('127.0.0.1')
          ) {
            actualUrl = actualUrl.replace(
              /http(s)?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/g,
              'https://baws.in'
            );
          }

          // Provide haptic feedback
          if (navigator.vibrate) navigator.vibrate(50);

          window.isActionSheetOpen = true;

          // Show Action Sheet
          const result = await ActionSheet.showActions({
            title: actualUrl,
            options: [
              {
                title: 'Copy Link Address',
                style: ActionSheetButtonStyle.Default,
              },
              {
                title: 'Open in Browser',
                style: ActionSheetButtonStyle.Default,
              },
              { title: 'Cancel', style: ActionSheetButtonStyle.Cancel },
            ],
          });

          window.isActionSheetOpen = false;

          if (result.index === 0) {
            // Copy Link
            try {
              await navigator.clipboard.writeText(actualUrl);
            } catch (err) {
              console.error('Failed to copy: ', err);
            }
          } else if (result.index === 1) {
            // Open in Browser
            await Browser.open({ url: actualUrl });
          }
        }
      };

      document.addEventListener('contextmenu', handleContextMenu);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      iframeRef,
      setIsIframeReady,
    }),
    [setIsIframeReady]
  ); // Dependency array ensures it only recalculates if the setter changes (which it won't)

  // const [uid, setUid] = useState("");
  const user = useSelector((state) => state.userReducer);

  useEffect(() => {
    // If consent already stored, set it and never show the banner.
    // Otherwise, show the consent UI after a 5 minute delay.
    let timerId = null;

    const checkConsent = () => {
      const storedConsent = localStorage.getItem('cookie_consent');
      if (storedConsent) {
        // If consent exists, apply it and cancel any pending timer
        setConsent(JSON.parse(storedConsent));
        setShowConsent(false);
        if (timerId) {
          clearTimeout(timerId);
          timerId = null;
        }
      } else {
        // No consent yet — schedule showing the banner after 5 minutes
        if (!timerId) {
          timerId = setTimeout(() => {
            setShowConsent(true);
            timerId = null;
          }, 300000); // 300,000 ms = 5 minutes
        }
      }
    };

    // Initial check
    checkConsent();

    // Only respond to storage events that change cookie_consent so we can cancel the timer if consent is given in another tab
    const storageListener = (e) => {
      if (e.key === 'cookie_consent') {
        checkConsent();
      }
    };

    window.addEventListener('storage', storageListener);

    return () => {
      window.removeEventListener('storage', storageListener);
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  // A function to allow the user to bring back the consent banner for changes.
  // You would typically link to this from your site's footer.
  const manageCookiePreferences = () => {
    localStorage.removeItem('cookie_consent');
    window.location.reload(); // Reload to re-trigger the consent banner
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userId = user.uid;

        if (userId !== '' && user.emailVerified) {
          localStorage.setItem('uid', userId); // TODO: Check if this localStorage is important / used anywhere?
          const userData = await getUserData(userId);

          if (userData && userData.isActive === false) {
            // Account is deactivated
            alert('Your account has been deactivated.');
            dispatch(userLogout());
            clearSavedFeed();
            auth.signOut();
            return;
          }

          dispatch(userLogin(userData));
          setUserId(analytics, userId);
          if (!highlightManager) {
            const manager = new HighlightManager(db, auth);
            setHighlightManager(manager);
          }
        }
      } else {
        // localStorage.removeItem("accessToken");
        dispatch(userLogout());
      }
    });

    return () => unsubscribe();
  }, []);

  //When user logs in for the first time and bookMarks are present in the localstorage
  useEffect(() => {
    const handleBookMarkToCloud = async () => {
      if (user.isLoggedIn) {
        if (localStorage.getItem('bookMarks')) {
          //take a backup copy in case we fail.  The user can contact us and we can tell them workaround.
          localStorage.setItem(
            'bookMarks_backup',
            localStorage.getItem('bookMarks')
          );
        }
        await checkUploadBookMarks();
      }
    };

    const handleFCMTokenToCloud = async () => {
      if (user.isLoggedIn) {
        if (localStorage.getItem('notificationToken')) {
          if (localStorage.getItem('FCMtokenUploadedToServer')) return;
          await checkUploadFCMToken();
        }
      }
    };

    handleBookMarkToCloud();
    handleFCMTokenToCloud();
  }, [user.isLoggedIn]);

  useEffect(() => {
    const handleStorageChange = async (event) => {
      if (event.storageArea === localStorage) {
        await checkUploadBookMarks();
        console.log('localStorage changed:');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      // Clean up the event listener when the component is unmounted
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Effect for listening to highlight changes from Firestore
  useEffect(() => {
    if (highlightManager) {
      // Start listening for real-time updates
      highlightManager.listenForHighlights((latestHighlights) => {
        console.log(
          'React Parent: Received updated highlights from Firestore.'
        );
        setHighlights(latestHighlights);
      });
    }

    // Cleanup: stop listening when the component unmounts
    return () => {
      if (highlightManager) {
        highlightManager.stopListening();
      }
    };
  }, [highlightManager]); // This effect runs when the manager is created

  // Effect for handling messages from the iframe
  useEffect(() => {
    const handleMessage = async (event) => {
      // It's good practice to check the origin for security
      // if (event.origin !== 'your-iframe-origin') return;

      const { type, payload } = event.data;

      if (type === 'iframeReady') {
        console.log('React Parent: iframe is ready. Finding it in the DOM.');
        setIsIframeReady(true);
        return; // Exit after handling this specific message
      }

      if (highlightManager) {
        switch (type) {
          case 'iframeUnloading':
            console.log('React Parent: iframe is unloading.');
            setIsIframeReady(false);
            break;
          case 'saveHighlight':
            console.log(
              'React Parent: Received request to save highlight.',
              payload
            );
            await highlightManager.addHighlight(payload);
            break;
          case 'deleteHighlight':
            console.log(
              'React Parent: Received request to delete highlight.',
              payload
            );
            await highlightManager.deleteHighlight(payload);
            break;
          default:
            // Ignore unknown messages
            break;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [highlightManager]); // Rerun if highlightManager changes

  // Effect to send highlights to the iframe whenever they are updated
  useEffect(() => {
    if (
      isIframeReady &&
      iframeRef.current &&
      iframeRef.current.contentWindow &&
      highlights
    ) {
      const timerId = setTimeout(() => {
        console.log(
          'React Parent: Sending highlights to iframe after 1s delay.'
        );
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'loadHighlights',
            payload: highlights,
          },
          '*' // In production, you should specify the iframe's origin
        );
      }, 5000); // 1000 millisecond delay

      // 2. Return a cleanup function to cancel the timer if dependencies change.
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [highlights, isIframeReady]); // This runs whenever highlights or iframe readiness changes

  return (
    <div>
      <IframeRefContext.Provider value={contextValue}>
        <RouterProvider router={router} />
      </IframeRefContext.Provider>
      {/* <script id="cookieyes" type="text/javascript" src="https://cdn-cookieyes.com/client_data/ffb2585b142d52a26dac4b61/script.js"></script> */}
      <EngagementTracker />
      {!consent && showConsent && <CookieConsent />}
    </div>
  );
}

export default App;
